// @ts-nocheck
import React, { useState, useRef, useEffect } from 'react';
import { Switch } from '@frontend/shadcn/components/ui/switch';
import {
  ListCheckIcon, ChevronUpIcon, CircleInfoIcon,
  GripVerticalIcon, PenIcon, PlusIcon,
  XmarkIcon
} from '@revfluence/fresh-icons/regular/esm';
import { cn } from '@frontend/shadcn/lib/utils';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import RichTextEditor from '@frontend/app/refresh-components/RichTextEditor';
import { useCustomStages } from '../context/CustomStagesContext';
import { FieldFormPopover } from './FieldFormPopover';
import { TaskSettingsSection } from './TaskSettingsSection';

interface FormField {
  id: string;
  label: string;
  type: 'text' | 'number' | 'select' | 'multiSelect' | 'yesNo' | 'checkbox' | 'date' | 'file' | 'url' | 'callToAction';
  required: boolean;
  options?: string[];
  description?: string;
  campaignOnly?: boolean;
}
interface Props {
  tasks: Array<{
    name: string;
    ctaLabel: string;
    creatorAccess: boolean;
    fields: FormField[];
  }>;
  setTasks: (tasks: Array<{
    name: string;
    ctaLabel: string;
    creatorAccess: boolean;
    fields: FormField[];
  }>) => void;
  taskSettings?: {
    initialStatuses: Array<{ id: number; name: string }>;
    finalStatus: { id: number; name: string } | null;
  };
  setTaskSettings: (settings: {
    initialStatuses: Array<{ id: number; name: string }>;
    finalStatus: { id: number; name: string } | null;
  }) => void;
}

export const TaskSection: React.FC<Props> = ({ tasks, setTasks, taskSettings, setTaskSettings }) => {
  const { selectedStage } = useCustomStages();
  const [isExpanded, setIsExpanded] = useState(true);

  const initializedStageKeyRef = useRef<string | null>(null);

  // Local state - this is the source of truth
  const [localTaskName, setLocalTaskName] = useState('');
  const [localCreatorAccess, setLocalCreatorAccess] = useState(false);
  const [localFormFields, setLocalFormFields] = useState<FormField[]>([]);
  const [showFieldForm, setShowFieldForm] = useState(false);
  const [editingFieldId, setEditingFieldId] = useState<string | null>(null);

  // Initialize local state from props ONLY when stage changes
  useEffect(() => {
    const currentStageKey = `${selectedStage?.id || 'new'}`;

    // Only initialize if this is a different stage or first time
    if (initializedStageKeyRef.current !== currentStageKey) {
      initializedStageKeyRef.current = currentStageKey;

      // Initialize from props or defaults
      const taskFromProps = tasks?.[0];
      if (taskFromProps) {
        setLocalTaskName(taskFromProps.name || 'Task Details');
        setLocalCreatorAccess(taskFromProps.creatorAccess || false);
        setLocalFormFields(taskFromProps.fields || []);
      } else {
        setLocalTaskName('Task Details');
        setLocalCreatorAccess(false);
        setLocalFormFields([]);
      }
    }
  }, [selectedStage?.id, tasks]); // Depend on stage ID and tasks

  // Sync local state to parent whenever it changes
  useEffect(() => {
    // Skip if not initialized yet
    if (initializedStageKeyRef.current === null) {
      return;
    }

    const updatedTask = {
      name: localTaskName,
      ctaLabel: 'Submit',
      creatorAccess: localCreatorAccess,
      fields: localFormFields
    };

    setTasks([updatedTask]);
  }, [localTaskName, localCreatorAccess, localFormFields, setTasks]);

  // Event handlers - these only update local state
  const handleNameChange = (name: string) => {
    setLocalTaskName(name);
  };

  const handleCreatorAccessChange = (access: boolean) => {
    setLocalCreatorAccess(access);
  };

  const handleAddField = () => {
    setShowFieldForm(true);
  };

  const handleEditField = (fieldId: string) => {
    setEditingFieldId(fieldId);
    setShowFieldForm(true);
  };

  const handleDeleteField = (fieldId: string) => {
    setLocalFormFields((prevFields) => prevFields.filter((f) => f.id !== fieldId));
  };

  const getInternalFieldType = (displayType: string): FormField['type'] => {
    switch (displayType) {
      case 'Free Text':
        return 'text';
      case 'Number':
        return 'number';
      case 'Single Select':
        return 'select';
      case 'Multi Select':
        return 'multiSelect';
      case 'Yes/No':
        return 'yesNo';
      case 'Checkbox':
        return 'checkbox';
      case 'Date':
        return 'date';
      case 'Upload File':
        return 'file';
      case 'URL':
        return 'url';
      case 'Call to Action':
        return 'callToAction';
      default:
        return 'text';
    }
  };

  const handleSaveField = (fieldData: {
    name: string;
    type: string;
    campaignOnly: boolean;
    options?: string[];
    description?: string;
  }) => {
    const updatedFields = editingFieldId
      ? localFormFields.map((field) =>
          (field.id === editingFieldId
            ? {
                ...field,
                label: fieldData.name,
                type: getInternalFieldType(fieldData.type),
                options: fieldData.options,
                description: fieldData.description,
                campaignOnly: fieldData.campaignOnly,
              }
            : field)
        )
      : [
          ...localFormFields,
          {
            id: String(Date.now()),
            label: fieldData.name,
            type: getInternalFieldType(fieldData.type),
            required: false,
            options: fieldData.options,
            description: fieldData.description,
            campaignOnly: fieldData.campaignOnly,
          }
        ];

    setLocalFormFields(updatedFields);
    setShowFieldForm(false);
    setEditingFieldId(null);
  };

  const getFieldTypeDisplay = (type: FormField['type']): string => {
    switch (type) {
      case 'text':
        return 'Free Text';
      case 'number':
        return 'Number';
      case 'select':
        return 'Single Select';
      case 'multiSelect':
        return 'Multi Select';
      case 'yesNo':
        return 'Yes/No';
      case 'checkbox':
        return 'Checkbox';
      case 'date':
        return 'Date';
      case 'file':
        return 'Upload File';
      case 'url':
        return 'URL';
      case 'callToAction':
        return 'Call to Action';
      default:
        return type;
    }
  };

  const getEditingField = () => {
    if (!editingFieldId) return null;
    const field = localFormFields.find((f) => f.id === editingFieldId);
    if (!field) return null;

    return {
      name: field.label,
      type: getFieldTypeDisplay(field.type),
      campaignOnly: field.campaignOnly || false,
      description: field.description,
      options: field.options,
    };
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(localFormFields);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setLocalFormFields(items);
  };

  const handleTaskSettingsChange = (settings: {
    initialStatuses: Array<{ id: number; name: string }>;
    finalStatus: { id: number; name: string } | null;
  }) => {
    setTaskSettings(settings);
  };

  return (
    <>
      <div className="p-6 bg-white rounded-2xl border border-gray-200 flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-orange-400 rounded-lg flex items-center justify-center">
              <ListCheckIcon className="w-3 h-3 text-white" />
            </div>
            <span className="flex-1 text-base font-medium text-gray-800">Task</span>

            <div className="flex items-center gap-2">
              <Switch
                checked={localCreatorAccess}
                onCheckedChange={handleCreatorAccessChange}
                className="bg-gray-800"
              />
              <span className="text-sm text-gray-800">Creator Access</span>
              <CircleInfoIcon className="w-3 h-3 text-gray-300" />
            </div>

            <button
              className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              <ChevronUpIcon className={cn('w-3.5 h-3.5 text-gray-800 transition-transform', !isExpanded && 'rotate-180')} />
            </button>
          </div>
          <p className="text-sm text-gray-500">
            Outline all necessary details and form fields. This task may be completed by creators and made visible in the Aspire Creator Portal.
          </p>
        </div>

        {isExpanded && (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-1">
                <span className="text-xs font-medium text-gray-800">Details</span>
                <CircleInfoIcon className="w-3 h-3 text-gray-300" />
              </div>
              <RichTextEditor
                value={localTaskName}
                onChange={handleNameChange}
                className="[&_.ql-editor]:min-h-[100px]"
                initialValue={localTaskName}
              />
            </div>

            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-800">Form Fields</span>
                <button
                  className="h-6 flex items-center gap-2 text-sm font-medium text-gray-800"
                  onClick={handleAddField}
                >
                  <PlusIcon className="w-3.5 h-3.5" />
                  Add Field
                </button>
              </div>

              {localFormFields.length === 0 ? (
                <div data-layout="Default" data-size="Small" data-type="Empty" className="self-stretch inline-flex justify-center items-center">
                  <div data-type="Small - Filled" className="w-80 p-2 bg-white flex justify-center items-start gap-2.5">
                    <div className="flex-1 text-center justify-start text-gray-500 text-sm font-normal font-['Inter'] leading-normal">
                      Add a form field.
                    </div>
                  </div>
                </div>
              ) : (
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="form-fields">
                    {(provided) => (
                      <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        className="flex flex-col gap-2"
                      >
                        {localFormFields.map((field, index) => (
                          <Draggable
                            key={field.id}
                            draggableId={field.id}
                            index={index}
                          >
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className={cn(
                                  'flex items-center gap-2 p-2 border border-gray-200 rounded-xl',
                                  snapshot.isDragging && 'bg-gray-50'
                                )}
                              >
                                <div
                                  {...provided.dragHandleProps}
                                  className="cursor-grab"
                                >
                                  <GripVerticalIcon className="w-3 h-3 text-gray-300" />
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center gap-1">
                                    <span className="text-sm font-medium text-gray-800">
                                      {field.label}
                                    </span>
                                    {field.required && (
                                      <span className="text-sm text-red-500">*</span>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-1">
                                  <button
                                    className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100"
                                    onClick={() => handleEditField(field.id)}
                                  >
                                    <PenIcon className="w-3.5 h-3.5 text-gray-800" />
                                  </button>
                                  <button
                                    className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100"
                                    onClick={() => handleDeleteField(field.id)}
                                  >
                                    <XmarkIcon className="w-3.5 h-3.5 text-red-400" />
                                  </button>
                                </div>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              )}
            </div>
          </div>
        )}
      </div>

      <TaskSettingsSection
        creatorAccess={localCreatorAccess}
        statuses={selectedStage?.statuses || []}
        selectedTaskStatuses={taskSettings?.initialStatuses || []}
        selectedResultingStatus={taskSettings?.finalStatus || null}
        onTaskSettingsChange={handleTaskSettingsChange}
      />

      {showFieldForm && (
        <FieldFormPopover
          onClose={() => {
            setShowFieldForm(false);
            setEditingFieldId(null);
          }}
          onSave={handleSaveField}
          initialData={editingFieldId ? getEditingField() || undefined : undefined}
        />
      )}
    </>
  );
};
